// Canvas 2D renderer for a sim `World`. Ported 1:1 from the v10 prototype's
// `draw()` — split into one render pass per file (field, traffic, fx,
// radar), each a set of plain functions; this class owns the mutable
// cosmetic state (trails, ripples, doodles, water cache, timing ring) and
// calls the passes in the same order the prototype drew them. No game logic
// lives here — the renderer only ever reads `World`.
import type { World, SimEvent, Vec } from '../sim/index.ts';
import { SIM_DT } from '../sim/index.ts';
import { drawMotif } from './decor.ts';
import { drawWater, drawGrass, drawTaxiway, drawRunway, drawPad, type WaterCache } from './field.ts';
import { drawPlane, type TrailDot } from './traffic.ts';
import { drawRipples, drawScores, drawDoodles, DOODLE_SPACING, RIPPLE_LIFE, SCORE_LIFE, type Ripple, type ScoreMark, type InkDot } from './fx.ts';
import { drawRadar } from './radar.ts';

export { doodleFade } from './fx.ts';

const TRAIL_MAX = 24;
const TRAIL_INTERVAL = 0.14;
const TRAIL_DECAY = 0.22;

/** Ring buffer size for `renderStats()` — 240 frames is 4s at 60fps, long
 *  enough to smooth out a single GC pause without going stale. */
const RENDER_STATS_FRAMES = 240;

export interface RenderStats { frames: number; meanMs: number; p95Ms: number; maxMs: number }

/** Per-frame context every render pass reads: the canvas 2D context plus the
 *  frame's own size and clock. Allocated once by the Renderer and mutated in
 *  place each `render()` call — passes take this instead of separate (ctx,
 *  W, H, now) parameters so adding a field later touches one place, and no
 *  fresh options object is built on the hot path. */
export interface FrameCtx { ctx: CanvasRenderingContext2D; W: number; H: number; now: number }

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  // ripples and trails are cosmetic-only state the renderer keeps for
  // itself, fed from `world.events` — the sim has no idea they exist.
  private ripples: Ripple[] = [];
  private scores: ScoreMark[] = [];
  private trails = new Map<number, TrailDot[]>();
  private trailT = new Map<number, number>();
  // Idle ink, oldest stroke first, each stroke's points in draw order.
  // `liveDoodle` is the one under the finger: `doodlePoint` pushes into it by
  // reference, so it is kept in the list even after a still hand has let every
  // one of its points expire.
  private doodles: InkDot[][] = [];
  private liveDoodle: InkDot[] | null = null;

  // Q7b: cheap CPU-time ring buffer around `render()` — two `performance.now()`
  // calls and a fixed-size array write per frame, so it costs essentially
  // nothing when `renderStats()` is never read. Preallocated, not a growing
  // array, so recording a sample never allocates.
  private renderMs = new Float64Array(RENDER_STATS_FRAMES);
  private renderMsCount = 0;
  private renderMsIdx = 0;

  // Route scratch (Q7b): the drawn route is the plane's own waypoint list
  // with its live position at the head. Reused every frame instead of
  // `[{x, y}, ...p.path]`, which allocated both a fresh head point and a
  // fresh spread-copied array every frame, per routed plane.
  private routeHead: Vec = { x: 0, y: 0 };
  private routeScratch: Vec[] = [];

  // Water polygon scratch (Q7b): `level.water` is a fixed set of screen
  // fractions: same polygon every frame for the lifetime of a World at a
  // given size. Cached on first use per (W, H), instead of re-mapping the
  // fraction list into fresh point arrays every frame.
  private waterCache: WaterCache | null = null;

  // The frame context passed to every render pass — see `FrameCtx`. `ctx`
  // never changes after construction; `W`/`H`/`now` are overwritten at the
  // top of every `render()` instead of building a fresh object per frame.
  private frame: FrameCtx;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d canvas context unavailable');
    this.ctx = ctx;
    this.frame = { ctx, W: 0, H: 0, now: 0 };
  }

  /** Clear cosmetic state. Call when swapping in a fresh `World` (aircraft ids restart at 1, so stale trails/ripples would otherwise leak into the new game). */
  reset(): void {
    this.ripples = [];
    this.scores.length = 0;
    this.trails.clear();
    this.trailT.clear();
    this.doodles.length = 0;
    this.liveDoodle = null;
    this.waterCache = null;
  }

  /** Open a stroke of idle ink — a finger went down away from every aircraft. */
  doodleStart(): void {
    this.liveDoodle = [];
    this.doodles.push(this.liveDoodle);
  }

  /** Extend the open stroke of idle ink with a point in logical px. */
  doodlePoint(x: number, y: number): void {
    const s = this.liveDoodle;
    if (!s) return;
    const last = s[s.length - 1];
    if (last && Math.hypot(x - last.x, y - last.y) < DOODLE_SPACING) return;
    s.push({ x, y, born: performance.now() });
  }

  /**
   * CPU time spent in `render()` over up to the last `RENDER_STATS_FRAMES`
   * frames (Q7b). Diagnostic only — call from a test/console, not from the
   * hot path.
   */
  renderStats(): RenderStats {
    const n = this.renderMsCount;
    if (n === 0) return { frames: 0, meanMs: 0, p95Ms: 0, maxMs: 0 };
    const samples = Array.from(this.renderMs.subarray(0, n)).sort((a, b) => a - b);
    const sum = samples.reduce((a, b) => a + b, 0);
    const p95 = samples[Math.min(n - 1, Math.floor(n * 0.95))];
    return { frames: n, meanMs: sum / n, p95Ms: p95, maxMs: samples[n - 1] };
  }

  private recordRenderTime(ms: number): void {
    this.renderMs[this.renderMsIdx] = ms;
    this.renderMsIdx = (this.renderMsIdx + 1) % RENDER_STATS_FRAMES;
    if (this.renderMsCount < RENDER_STATS_FRAMES) this.renderMsCount++;
  }

  /** Size the backing store for a CSS viewport. `scale` is the sim's `viewScale(cssW,cssH)`. */
  resize(cssW: number, cssH: number, dpr: number, scale: number): void {
    const d = Math.min(dpr, 2);
    this.canvas.width = Math.max(1, Math.round(cssW * d));
    this.canvas.height = Math.max(1, Math.round(cssH * d));
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(d * scale, 0, 0, d * scale, 0, 0);
  }

  /** Call once per `step()` — ages ripples/trails by exactly SIM_DT and ingests that step's events. */
  onStep(world: World): void {
    for (const e of world.events) this.ingestEvent(e);
    // age + drop expired ripples in place — `.filter()` would allocate a
    // fresh array every single sim step regardless of whether anything
    // actually expired.
    let w = 0;
    for (let i = 0; i < this.ripples.length; i++) {
      const r = this.ripples[i];
      r.t += SIM_DT;
      if (r.t < RIPPLE_LIFE[r.kind]) this.ripples[w++] = r;
    }
    this.ripples.length = w;
    let sw = 0;
    for (let i = 0; i < this.scores.length; i++) {
      const m = this.scores[i];
      m.t += SIM_DT;
      if (m.t < SCORE_LIFE) this.scores[sw++] = m;
    }
    this.scores.length = sw;
    this.updateTrails(world);
  }

  private ingestEvent(e: SimEvent): void {
    if (e.t === 'landed') {
      this.ripples.push({ x: e.x, y: e.y, t: 0, kind: 'landed' });
      this.scores.push({ x: e.x, y: e.y, text: `+${e.points}`, t: 0 });
    }
    else if (e.t === 'goaround') this.ripples.push({ x: e.x, y: e.y, t: 0, kind: 'goaround' });
    else if (e.t === 'crash') this.ripples.push({ x: e.x, y: e.y, t: 0, kind: 'crash', wallStart: performance.now() });
  }

  private updateTrails(world: World): void {
    const live = new Set<number>();
    for (const p of world.planes) {
      live.add(p.id);
      let dots = this.trails.get(p.id);
      if (!dots) { dots = []; this.trails.set(p.id, dots); }
      if (p.rollout === 0) {
        let t = (this.trailT.get(p.id) ?? 0) + SIM_DT;
        if (t > TRAIL_INTERVAL) {
          t = 0;
          if (dots.length >= TRAIL_MAX) {
            // recycle the oldest dot's object instead of allocating a new
            // one and letting `shift()` discard it — once a trail is warm
            // (after TRAIL_MAX * TRAIL_INTERVAL ≈ 3.4s) this never allocates.
            const recycled = dots.shift()!;
            recycled.x = p.x; recycled.y = p.y; recycled.a = 0.85;
            dots.push(recycled);
          } else {
            dots.push({ x: p.x, y: p.y, a: 0.85 });
          }
        }
        this.trailT.set(p.id, t);
        for (const d of dots) d.a -= SIM_DT * TRAIL_DECAY;
      }
    }
    for (const id of this.trails.keys()) {
      if (!live.has(id)) { this.trails.delete(id); this.trailT.delete(id); }
    }
  }

  /**
   * Draw one frame. `dragPlaneId` is the aircraft currently being dragged, if
   * any (brighter path guide). `showRadar` defaults to `world.state` but the
   * app overrides it while a level hasn't actually started yet — a freshly
   * created `World` is always `'playing'` even before the player has tapped
   * "tap to begin" (the sim has no notion of an intro screen).
   */
  render(world: World, dragPlaneId: number | null = null, showRadar?: boolean): void {
    const t0 = performance.now();
    const { ctx, frame } = this;
    const { W, H, level: lv } = world;
    frame.W = W; frame.H = H; frame.now = t0;
    ctx.clearRect(0, 0, W, H);

    ctx.fillStyle = lv.palette.bg;
    ctx.fillRect(0, 0, W, H);
    if (lv.water) this.waterCache = drawWater(frame, world, this.waterCache);

    for (const r of world.runways) drawGrass(frame, r, lv.palette.field);
    if (world.runways.length > 1) drawTaxiway(frame, world.runways[0], world.runways[1], lv.palette.deep);
    for (const r of world.runways) drawRunway(frame, r);
    for (const pd of world.pads) drawPad(frame, pd);

    for (const id of lv.decor) drawMotif(ctx, id, W, H);

    // idle ink first: it is scenery, so everything that means something —
    // ripples, routes, aircraft, radar — is drawn over it.
    drawDoodles(frame, this.doodles, this.liveDoodle);
    drawRipples(frame, this.ripples);
    drawScores(frame, this.scores);

    for (const p of world.planes) drawPlane(frame, p, dragPlaneId === p.id, this.trails, this.routeHead, this.routeScratch);

    const radar = showRadar ?? (world.state === 'playing' || world.state === 'dead');
    if (radar) drawRadar(frame, world);
    this.recordRenderTime(performance.now() - t0);
  }
}
